/**
 * Tooltips show a hint or helper text to describe an element on hover/focus
 * @see WAI-ARIA https://www.w3.org/TR/wai-aria-practices-1.2/#tooltip
 */
import classNames from "classnames";
import React, {
  ReactElement,
  RefObject,
  Component,
  createRef,
  CSSProperties,
  ReactNode,
  MouseEvent,
  KeyboardEvent,
  cloneElement,
  Children,
  FocusEvent,
  DetailedHTMLProps,
  HTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { action, observable, computed } from "mobx";
import { observer } from "mobx-react";
import { Transition } from "react-transition-group";
import { TransitionStatus } from "react-transition-group/Transition";
import forceReflow from "../../../utils/forceReflow";
import getPortalRoot from "../../../utils/getPortalRoot";
import { AnimationDuration } from "../../../styles/Animations";
import { randomString } from "../../../utils";
import getOverlayPosition, { PositionVector, OverlayPositionProps } from "../../../utils/getOverlayPosition";
import observeRect from "@reach/observe-rect";
import { isEqual } from "lodash";

export enum TooltipSize {
  Small = "small",
  Large = "large",
}

export enum TooltipPosition {
  Top = "top",
  Bottom = "bottom",
}

export interface ITooltipProps {
  /**
   * Content or text to render inside the tooltip
   */
  content: JSX.Element | string;
  /**
   * Render the tooltip on the top/bottom of the trigger element
   */
  position?: TooltipPosition;
  /**
   * When a `show` prop is passed in, the Tooltip changes from one that shows on hover
   * to one that is controlled by the `show` prop
   */
  show?: boolean;
  /**
   * Use `small` for 1—2 word hints, and `large` for longer sentence descriptions
   */
  size?: TooltipSize;
  /**
   * The distance between the tooltip and trigger element
   */
  offset?: number;
  /**
   * Avoid using this for any interactive target elements. Wraps the target in a div to handle
   * interaction events and accessibility instead of using the default `cloneElement` to pass them
   * directly. Only use this for target components that don't accept `onMouseEnter`, `onMouseLeave`,
   * `onFocus`, `onBlur`, and `aria-labelledby`.
   *
   * There is an exception to the above - when using an interactive target element that
   * can be marked as disabled, not all mouse events fire correctly from the disabled event,
   * and the `Tooltip` will intermittently stay open indefinitely.
   */
  wrapTarget?: boolean;
  /**
   * The className given to the target container
   */
  targetClassName?: string;
  /**
   * Applies role="alert" for error tooltips
   */
  isError?: boolean;
  /**
   * The className given to the tooltip element
   */
  className?: string;
}

/**
 * Tooltip component. Child component must accept onMouseEnter, onMouseLeave, onFocus,
 * onBlur, and onKeyDown props for it to trigger and be accessible
 */
@observer
export default class Tooltip extends Component<ITooltipProps> {
  public static defaultProps = {
    size: TooltipSize.Small,
    position: TooltipPosition.Top,
    offset: 8,
  };

  private readonly targetRef: RefObject<HTMLDivElement> = createRef();
  private readonly portalRoot = getPortalRoot("tooltip-root");
  private readonly id = randomString();

  @observable
  private show = false;

  public render(): ReactElement {
    const { content, size, children, className, wrapTarget, targetClassName, position, offset, isError } = this.props;
    const show = this.isControlledTooltip ? this.props.show : this.show;
    const tooltipId = `tooltip-${this.id}`;

    // Only accept a single child node when using `cloneElement`
    const child = wrapTarget ? null : (Children.only(children) as ReactElement);

    // Props passed to either the wrapper div when using `wrapTarget`, or to the cloned element.
    const targetProps: DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> = {
      ["aria-describedby"]: show ? tooltipId : undefined,
      tabIndex: isError ? undefined : 0,
      onFocus: (event) => this.handleEnter(event, child?.props.onFocus),
      onBlur: (event) => this.handleLeave(event, child?.props.onBlur),
      onMouseEnter: (event) => this.handleEnter(event, child?.props.onMouseEnter),
      onMouseLeave: (event) => this.handleLeave(event, child?.props.onMouseLeave),
      onKeyDown: (event) => this.handleKeyDown(event, child?.props.onKeyDown),
    };

    return (
      <div className={classNames("kl-tooltip-target", targetClassName)} ref={this.targetRef}>
        <Transition mountOnEnter unmountOnExit in={show} timeout={AnimationDuration.Medium} onEnter={forceReflow}>
          {(status): ReactElement =>
            createPortal(
              <TooltipElement
                id={tooltipId}
                targetRef={this.targetRef}
                position={position}
                size={size}
                onMouseEnter={this.handleEnter}
                onMouseLeave={this.handleLeave}
                className={className}
                transitionStatus={status}
                offset={offset}
                isError={isError}
              >
                {content}
              </TooltipElement>,
              this.portalRoot as Element,
            )
          }
        </Transition>
        {child == null ? (
          <div className="kl-tooltip-target__wrapper" {...targetProps}>
            {children}
          </div>
        ) : (
          cloneElement(child, targetProps)
        )}
      </div>
    );
  }

  @action
  private handleEnter = (
    event: FocusEvent<HTMLElement> | MouseEvent<HTMLElement>,
    handleEvent?: (event: FocusEvent<HTMLElement> | MouseEvent<HTMLElement>) => void,
  ): void => {
    handleEvent?.(event);
    this.show = true;
  };

  @action
  private handleLeave = (
    event: FocusEvent<HTMLElement> | MouseEvent<HTMLElement>,
    handleEvent?: (event: FocusEvent<HTMLElement> | MouseEvent<HTMLElement>) => void,
  ): void => {
    handleEvent?.(event);
    this.show = false;
  };

  @action
  private handleKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    handleEvent: (event: KeyboardEvent<HTMLElement>) => void,
  ): void => {
    handleEvent?.(event);
    if (event.key === "Escape") {
      this.show = false;
    }
  };

  @computed
  private get isControlledTooltip(): boolean {
    return this.props.show !== undefined && this.props.show !== null;
  }
}

interface ITooltipElementProps {
  id: string;
  targetRef?: RefObject<HTMLElement>;
  style?: CSSProperties;
  position?: TooltipPosition;
  size?: TooltipSize;
  onMouseEnter?: (event: MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (event: MouseEvent<HTMLElement>) => void;
  children: ReactNode;
  className?: string;
  caret?: PositionVector;
  popover?: PositionVector;
  offset?: number;
  transitionStatus?: TransitionStatus;
  isError?: boolean;
}

@observer
export class TooltipElement extends Component<ITooltipElementProps> {
  public static defaultProps = {
    transitionStatus: "entered",
  };

  private readonly tooltipRef: RefObject<HTMLDivElement> = createRef();
  private rectObserver: { observe: () => void; unobserve: () => void } | undefined;

  @observable.ref
  private tooltipPosition: OverlayPositionProps | undefined;

  public componentDidMount(): void {
    if (this.props.targetRef?.current) {
      this.rectObserver = observeRect(this.props.targetRef.current, this.handleRectObserver);
      this.rectObserver.observe();
    }
  }

  public componentDidUpdate(prevProps: ITooltipElementProps): void {
    if (!isEqual(prevProps, this.props) && this.props.targetRef?.current) {
      this.handleRectObserver(this.props.targetRef.current.getBoundingClientRect());
    }
  }

  public componentWillUnmount(): void {
    this.rectObserver?.unobserve();
  }

  public render(): ReactElement {
    const {
      id,
      style,
      size,
      className,
      children,
      onMouseEnter,
      onMouseLeave,
      transitionStatus,
      position,
      isError,
    } = this.props;
    const positionModifier = this.tooltipPosition?.modifier || position;

    return (
      <div
        id={id}
        ref={this.tooltipRef}
        style={{ ...style, ...this.tooltipPosition?.overlay }}
        contentEditable={false}
        className={classNames(
          "kl-tooltip",
          `kl-tooltip--${positionModifier}`,
          `kl-tooltip--${size}`,
          `kl-tooltip--${transitionStatus}`,
          className,
        )}
        role={isError ? "alert" : "tooltip"}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onMouseDown={this.onMouseDown}
      >
        <div className="kl-tooltip__content">{children}</div>
        <div
          className={classNames("kl-tooltip__caret", `kl-tooltip__caret--${positionModifier}`)}
          style={this.tooltipPosition?.caret}
        />
      </div>
    );
  }

  @action
  private handleRectObserver = (targetRect?: DOMRect): void => {
    const { position, offset } = this.props;
    const overlayRect = this.tooltipRef.current?.getBoundingClientRect();

    this.tooltipPosition = getOverlayPosition({
      position,
      offset,
      targetRect,
      overlayRect,
      caretSize: 6,
      outerMargin: 4,
    });
  };

  private onMouseDown = (event: MouseEvent): void => {
    event.preventDefault();
    event.stopPropagation();
  };
}
